import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SPEAKERS } from "@/constants/summit";
import { Reveal, MaskedLine } from "@/components/Reveal";

const CATEGORIES = ["All", ...new Set(SPEAKERS.map((s) => s.category))];

const Speakers = () => {
  const [filter, setFilter] = useState("All");
  const filtered = useMemo(
    () => (filter === "All" ? SPEAKERS : SPEAKERS.filter((s) => s.category === filter)),
    [filter]
  );

  return (
    <main data-testid="speakers-page" className="pt-[72px]">
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,87,34,0.12),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-brand-500" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-300">The Speakers</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.0]">
            <MaskedLine delay={0.1}>The minds taking</MaskedLine>
            <MaskedLine delay={0.24}><span className="text-brand-500">the stage.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed"
          >
            Clinicians, innovators, leaders and storytellers — each chosen for one reason:
            they are shaping what intelligent medicine becomes next.
          </motion.p>

          <div data-testid="speaker-filter-bar" className="mt-10 flex flex-wrap gap-2.5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`speaker-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`rounded-full border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                  filter === c
                    ? "border-brand-500 bg-brand-500 text-white shadow-[0_0_24px_rgba(255,87,34,0.4)]"
                    : "border-white/15 text-white/60 hover:border-brand-400/60 hover:text-brand-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <motion.div layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((s) => (
              <motion.div
                layout
                key={s.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      data-testid={`speaker-card-${s.id}`}
                      className="group block w-full overflow-hidden rounded-2xl border border-white/10 bg-charcoal text-left transition-all duration-500 hover:-translate-y-2 hover:border-brand-500/50 hover:shadow-[0_20px_60px_rgba(255,87,34,0.15)]"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img
                          src={s.image}
                          alt={s.name}
                          className="h-full w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                        <span className="absolute left-4 top-4 rounded-full bg-obsidian/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-300 backdrop-blur-sm">
                          {s.category}
                        </span>
                        <span className="absolute bottom-4 right-4 rounded-full bg-brand-500 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          View bio
                        </span>
                      </div>
                      <div className="p-5">
                        <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                        <p className="mt-1 text-sm text-brand-300">{s.role}</p>
                        <p className="text-xs text-white/45">{s.organization}</p>
                      </div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="border-white/10 bg-charcoal text-white sm:max-w-lg" data-testid={`speaker-bio-modal-${s.id}`}>
                    <DialogHeader>
                      <div className="flex items-center gap-4">
                        <img src={s.image} alt={s.name} className="h-16 w-16 rounded-xl object-cover border border-white/15" />
                        <div>
                          <DialogTitle className="font-display text-xl">{s.name}</DialogTitle>
                          <DialogDescription className="text-brand-300">{s.role}</DialogDescription>
                        </div>
                      </div>
                    </DialogHeader>
                    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/50">
                      <Building2 className="h-3.5 w-3.5 text-brand-400" /> {s.organization}
                    </p>
                    <p className="text-sm leading-relaxed text-white/70">{s.bio}</p>
                  </DialogContent>
                </Dialog>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-t border-white/10 bg-charcoal/40 py-16">
        <Reveal className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 sm:flex-row sm:items-center sm:px-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Hear them live — in the room.</h2>
            <p className="mt-2 text-sm text-white/55">Attendance is strictly VVIP. Confirm yours before capacity closes.</p>
          </div>
          <Link
            to="/confirm-attendance"
            data-testid="speakers-page-rsvp-button"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 font-display text-sm font-semibold transition-all duration-300 hover:bg-brand-600 hover:shadow-[0_0_40px_rgba(255,87,34,0.55)]"
          >
            Confirm Attendance
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default Speakers;
